import { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import VideoModal from "./VideoModal";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_medboard-exec/artifacts/v9vlt7y2_MEDSIM%20hospital%20exterior.png";
const SIMULATOR_URL = process.env.REACT_APP_SIMULATOR_URL || "#";

const heroTextAnimation = {
  initial: { opacity: 0, x: -40 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8, ease: "easeOut" },
};

const heroImageAnimation = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8, delay: 0.2, ease: "easeOut" },
};

export default function Hero() {
  const { t, lang } = useLanguage();
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f1f] via-[#0a0f1f] to-[#0d1a3a]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Text */}
          <motion.div
            {...heroTextAnimation}
          >
            <p
              data-testid="hero-tagline"
              className="font-outfit text-sm md:text-base text-[#1a6cf5] tracking-wide mb-6 uppercase"
            >
              {t("hero.tagline")}
            </p>

            <h1
              data-testid="hero-h1"
              className="font-playfair text-5xl md:text-6xl lg:text-7xl text-[#f8f9fa] tracking-tight leading-[1.1] mb-8"
            >
              {t("hero.h1_line1")}
              <br />
              <span className="text-[#1a6cf5]">{t("hero.h1_line2")}</span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="font-outfit text-base md:text-lg text-[#94a3b8] max-w-lg mb-10 leading-relaxed"
            >
              {t("hero.subtitle")}
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="#features"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#1a6cf5] hover:bg-[#3b82f6] text-white font-outfit px-8 py-4 rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#1a6cf5]/25"
              >
                {t("hero.cta_primary")}
                <ArrowRight size={18} />
              </a>
              <button
                onClick={() => setVideoOpen(true)}
                data-testid="hero-cta-video"
                className="inline-flex items-center gap-2 bg-[#2a3449] hover:bg-[#3a465f] text-white font-outfit px-8 py-4 rounded-full transition-all duration-300 hover:-translate-y-1"
              >
                <Play size={18} />
                {t("hero.cta_video")}
              </button>
            </div>

            <a
              href={SIMULATOR_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-simulator-btn"
              className="inline-flex items-center gap-2 mt-6 border border-[#1a6cf5]/40 text-[#1a6cf5] hover:bg-[#1a6cf5]/10 font-outfit px-8 py-4 rounded-full transition-all duration-300"
            >
              {t("nav.simulator")}
            </a>
          </motion.div>

          {/* Right Image */}
          <motion.div
            {...heroImageAnimation}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(26,108,245,0.15)]">
              <img
                src={HERO_IMG}
                alt="MedSim Hospital"
                data-testid="hero-image"
                className="w-full h-auto object-cover rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0a0f1f]/30 rounded-2xl" />
            </div>
            {/* Decorative glow */}
            <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-[#1a6cf5]/10 rounded-full blur-3xl pointer-events-none" />
          </motion.div>
        </div>
      </div>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} lang={lang} />
    </section>
  );
}
